/**
 * `rosterWeeks` — weekly-pagination geometry for the print view (#708).
 *
 * The print roster (`<RosterPrintView/>`) renders each calendar week as
 * its own page/section with a CSS page-break, repeated column headers,
 * and no interactive chrome. To do that it needs the roster's flat
 * `days[]` list (an arbitrary-length horizon, 1 week → multi-month)
 * split into ordered week buckets.
 *
 * Design choices (all pinned by `tests/rosterWeeks.test.ts`):
 *   - Weeks start on **Monday** (ISO-8601). A horizon that starts
 *     mid-week produces a *partial leading* week; one that ends
 *     mid-week produces a *partial trailing* week. Both are kept as
 *     real buckets — we never pad with phantom days, because the print
 *     grid only has columns for days the roster actually covers.
 *   - Bucketing keys off each day's own ISO `date` string, NOT the
 *     index, so a horizon with a gap (shouldn't happen, but defensive)
 *     still lands every day in its correct calendar week.
 *   - The input order is preserved within each bucket and buckets are
 *     emitted in chronological order of their Monday anchor.
 *
 * Pure module — no React, no DOM — so the pagination contract is unit-
 * testable in isolation (the rendering test stays light).
 */

/** Minimal day shape the bucketer needs — a superset-compatible subset
 * of the generated `RosterDay` (`{ date, weekday, is_weekend, ... }`).
 * Kept structural so callers pass `RosterResponse["days"]` directly. */
export interface WeekBucketDay {
  /** ISO `yyyy-mm-dd`. */
  date: string;
}

export interface RosterWeek<D extends WeekBucketDay> {
  /** ISO `yyyy-mm-dd` of the Monday that anchors this week (may be a
   * date earlier than any day present, when the week starts partial). */
  weekStartIso: string;
  /** 1-based ordinal of the week within the horizon (Week 1, Week 2…). */
  index: number;
  /** The roster days that fall in this week, in input order. */
  days: D[];
}

/**
 * Parse an ISO `yyyy-mm-dd` to a UTC `Date`. We parse explicitly (not
 * `new Date(iso)`) and use UTC throughout so the bucketing is immune to
 * the runner's / browser's local timezone — a roster day is a calendar
 * date, not an instant.
 */
function parseIsoUtc(iso: string): Date {
  const parts = iso.split("-");
  const y = Number.parseInt(parts[0] ?? "0", 10);
  const m = Number.parseInt(parts[1] ?? "1", 10);
  const d = Number.parseInt(parts[2] ?? "1", 10);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Format a UTC `Date` back to ISO `yyyy-mm-dd`. */
function toIsoUtc(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The ISO Monday on/before `iso`. `getUTCDay()` is 0 (Sun)…6 (Sat); the
 * offset to the preceding Monday is `(day + 6) % 7` (Mon→0, Sun→6).
 */
export function isoWeekStart(iso: string): string {
  const date = parseIsoUtc(iso);
  const offset = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - offset);
  return toIsoUtc(date);
}

/** #2554 — the day the phone calendar's week starts on (the lead's laptop stays Monday). */
export type WeekStart = "sun" | "mon";

/**
 * The first day of `iso`'s week on/before it, for the phone calendar's
 * member setting: Sunday (`sun`) or Monday (`mon`, same as `isoWeekStart`).
 */
export function weekStartOf(iso: string, start: WeekStart): string {
  if (start === "mon") return isoWeekStart(iso);
  const date = parseIsoUtc(iso);
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return toIsoUtc(date);
}

/**
 * ISO date arithmetic: `iso + days` (days may be negative). UTC-pure
 * like the rest of this module — a roster day is a calendar date, not
 * an instant. Added for the #1601 member week pager (prev/next week +
 * fetch-window bounds); shares the parse/format helpers above.
 */
export function addDaysIso(iso: string, days: number): string {
  const date = parseIsoUtc(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return toIsoUtc(date);
}

/**
 * Split an ordered list of roster days into Monday-anchored week
 * buckets. Partial leading / trailing weeks are preserved as their own
 * buckets. Buckets come out in chronological order of their Monday
 * anchor; the `index` field is the 1-based week ordinal for "Week N of
 * M" print headers.
 *
 * An empty input yields an empty list (the caller renders an empty
 * state, not a phantom Week 1).
 */
export function splitDaysIntoWeeks<D extends WeekBucketDay>(days: readonly D[]): RosterWeek<D>[] {
  if (days.length === 0) return [];

  // Group by the day's own Monday anchor so a non-contiguous horizon
  // still buckets correctly.
  const byWeekStart = new Map<string, D[]>();
  for (const day of days) {
    const ws = isoWeekStart(day.date);
    const bucket = byWeekStart.get(ws);
    if (bucket) {
      bucket.push(day);
    } else {
      byWeekStart.set(ws, [day]);
    }
  }

  // Emit chronologically by Monday anchor. ISO `yyyy-mm-dd` strings sort
  // lexicographically == chronologically.
  const orderedStarts = [...byWeekStart.keys()].sort();
  return orderedStarts.map((weekStartIso, i) => ({
    weekStartIso,
    index: i + 1,
    days: byWeekStart.get(weekStartIso) ?? [],
  }));
}
